import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MagnifyingGlassIcon, Cog6ToothIcon, CheckIcon } from '@heroicons/react/24/outline';
import SkillIcon from '../icons/SkillIcon';
import { i18nService } from '../../services/i18n';
import { skillService } from '../../services/skill';
import { RootState } from '../../store';
import { Skill } from '../../types/skill';
import { addSkill } from '../../store/slices/skillSlice';
import { getBuiltinEnabledMap } from './BuiltinSkillsPanel';

interface SkillsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSkill: (skill: Skill) => void;
  onManageSkills: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

const SkillsPopover: React.FC<SkillsPopoverProps> = ({
  isOpen,
  onClose,
  onSelectSkill,
  onManageSkills,
  anchorRef,
}) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [maxListHeight, setMaxListHeight] = useState(256); // default max-h-64 = 256px
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const personalSkills = useSelector((state: RootState) => state.skill.skills);
  const activeSkillIds = useSelector((state: RootState) => state.skill.activeSkillIds);
  const [distributedSkills, setDistributedSkills] = useState<Skill[]>([]);
  const [language, setLanguage] = useState(i18nService.getLanguage());

  useEffect(() => i18nService.subscribe(() => setLanguage(i18nService.getLanguage())), []);

  // 加载 platform 分发的内置技能 + 启用状态
  useEffect(() => {
    if (!isOpen) return;
    Promise.all([
      skillService.getDistributedSkills(),
      (window.electron.skills as any).getBuiltinEnabledState().catch(() => ({ data: {} })),
    ]).then(([result, enabledResult]) => {
      if (result.success && result.data) {
        const items = result.data?.data || result.data || [];
        if (!Array.isArray(items)) { setDistributedSkills([]); return; }
        const enabledMap: Record<string, boolean> = enabledResult?.data || getBuiltinEnabledMap();
        const converted: Skill[] = items.map((ds: any) => {
          const rawSlug = ds.skill_slug || ds.skill_name || String(ds.skill_id);
          // 加 system: 前缀避免与个人技能同 slug 碰撞
          const skillId = `system:${rawSlug}`;
          const enableKey = ds.skill_slug || ds.skill_name || String(ds.skill_id);
          return {
            id: skillId,
            name: ds.skill_name || ds.display_name || ds.skill_slug || '',
            description: ds.skill_summary || ds.summary || '',
            enabled: enabledMap[enableKey] !== false,
            isOfficial: false,
            isBuiltIn: true,
            updatedAt: ds.created_at ? new Date(ds.created_at).getTime() : Date.now(),
            prompt: '',
            // 系统技能用标记路径，后端据此重写为沙箱内 public-skill 路径
            skillPath: `system-skill://${rawSlug}/SKILL.md`,
            category: ds.skill_tag || '',
            source: 'system' as const,
          };
        });
        setDistributedSkills(converted);
      }
    });
  }, [isOpen, language]);

  // 合并个人技能 + platform 分发的内置技能
  // CE edition: 当无 platform 分发技能时，本地内置技能直接展示
  const skills = useMemo(() => {
    const hasDistributed = distributedSkills.length > 0;
    const personal = personalSkills
      .filter(s => hasDistributed ? !s.isBuiltIn : s.enabled !== false)
      .map(s => ({ ...s, _uniqueKey: `personal:${s.id}` }));
    const builtin = distributedSkills
      .map(s => ({ ...s, _uniqueKey: `builtin:${s.id}` }));
    return [...personal, ...builtin];
  }, [personalSkills, distributedSkills]);

  // 构建 name → 已选中的 uniqueKey 映射，用于重名互斥
  const selectedNameMap = useMemo(() => {
    const map = new Map<string, string>(); // name -> selected uniqueKey
    for (const skill of skills) {
      if (activeSkillIds.includes(skill.id)) {
        // 对于重名技能，记录当前选中的那个
        const name = skill.name.toLowerCase();
        if (!map.has(name)) {
          map.set(name, (skill as any)._uniqueKey);
        }
      }
    }
    return map;
  }, [skills, activeSkillIds]);

  // 判断技能是否因重名互斥而被禁用
  const isDisabledByConflict = (skill: any): boolean => {
    const name = skill.name.toLowerCase();
    const selectedKey = selectedNameMap.get(name);
    // 如果有同名技能被选中，且不是自己，则置灰
    return !!selectedKey && selectedKey !== skill._uniqueKey;
  };

  const categoryLabelMap: Record<string, string> = useMemo(() => ({
    '文档处理': i18nService.t('categoryDocuments'),
    '搜索资讯': i18nService.t('categorySearch'),
    '设计创作': i18nService.t('categoryDesign'),
    '效率工具': i18nService.t('categoryTools'),
    '写作助手': i18nService.t('categoryWriting'),
    '开发集成': i18nService.t('categoryDev'),
    'productivity': i18nService.t('categoryTools'),
    'development': i18nService.t('categoryDev'),
    'writing': i18nService.t('categoryWriting'),
    'analysis': i18nService.t('categoryAnalysis'),
    'design': i18nService.t('categoryDesign'),
    'education': i18nService.t('categoryEducation'),
    'other': i18nService.t('categoryOther'),
  }), [language]);

  // Filter enabled skills based on search query
  const filteredSkills = skills
    .filter(s => s.enabled)
    .filter(s =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Group filtered skills by category
  const groupedSkills = useMemo(() => {
    const groups: { category: string; label: string; skills: typeof filteredSkills }[] = [];
    const categoryOrder: string[] = [];
    const categoryMap: Record<string, typeof filteredSkills> = {};

    filteredSkills.forEach(skill => {
      const cat = skill.category || '';
      if (!categoryMap[cat]) {
        categoryMap[cat] = [];
        categoryOrder.push(cat);
      }
      categoryMap[cat].push(skill);
    });

    categoryOrder.forEach(cat => {
      groups.push({
        category: cat,
        label: cat ? (categoryLabelMap[cat] || cat) : i18nService.t('uncategorized'),
        skills: categoryMap[cat],
      });
    });

    return groups;
  }, [filteredSkills, categoryLabelMap]);

  const hasMultipleGroups = groupedSkills.length > 1 || (groupedSkills.length === 1 && groupedSkills[0].category !== '');

  // Calculate available height and focus search input when popover opens
  useEffect(() => {
    if (isOpen) {
      // Calculate available space above the anchor
      if (anchorRef.current) {
        const anchorRect = anchorRef.current.getBoundingClientRect();
        // Available height = distance from top of viewport to anchor, minus padding for search bar (~120px) and some margin (~60px)
        const availableHeight = anchorRect.top - 120 - 60;
        // Clamp between 120px (minimum usable) and 256px (default max)
        setMaxListHeight(Math.max(120, Math.min(256, availableHeight)));
      }
      if (searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen, anchorRef]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsidePopover = popoverRef.current?.contains(target);
      const isInsideAnchor = anchorRef.current?.contains(target);

      if (!isInsidePopover && !isInsideAnchor) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSelectSkill = (skill: Skill) => {
    // 系统精选技能不在 Redux skills 数组中，选中时写入，以便 CoworkPromptInput 构建 prompt 时能查到
    if (skill.source === 'system' && !personalSkills.find(s => s.id === skill.id)) {
      dispatch(addSkill(skill));
    }
    onSelectSkill(skill);
    // Don't close popover to allow multi-selection
  };

  const handleManageSkills = () => {
    onManageSkills();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className="absolute bottom-full left-0 mb-2 w-72 max-w-[90vw] rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface shadow-xl z-50"
    >
      {/* Search input */}
      <div className="p-3 border-b dark:border-claude-darkBorder border-claude-border">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={i18nService.t('searchSkills')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkText text-claude-text dark:placeholder-claude-darkTextSecondary placeholder-claude-textSecondary border dark:border-claude-darkBorder border-claude-border focus:outline-none focus:ring-2 focus:ring-claude-accent"
          />
        </div>
      </div>

      {/* Skills list */}
      <div className="overflow-y-auto py-1" style={{ maxHeight: `${maxListHeight}px` }}>
        {filteredSkills.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {i18nService.t('noSkillsAvailable')}
          </div>
        ) : (
          groupedSkills.map((group) => (
            <div key={group.category}>
              {hasMultipleGroups && (
                <div className="px-3 pt-2.5 pb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider dark:text-claude-darkTextSecondary text-claude-textSecondary">
                    {group.label}
                  </span>
                </div>
              )}
              {group.skills.map((skill) => {
                const isActive = activeSkillIds.includes(skill.id);
                const disabled = isDisabledByConflict(skill);
                return (
                  <button
                    key={(skill as any)._uniqueKey || skill.id}
                    onClick={() => !disabled && handleSelectSkill(skill)}
                    disabled={disabled}
                    className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors ${
                      disabled
                        ? 'opacity-40 cursor-not-allowed'
                        : isActive
                          ? 'dark:bg-claude-accent/10 bg-claude-accent/10'
                          : 'dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
                    }`}
                    title={disabled ? '已选择同名技能的其他版本' : undefined}
                  >
                    <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive
                        ? 'bg-claude-accent text-white'
                        : 'dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover'
                    }`}>
                      {isActive ? (
                        <CheckIcon className="h-4 w-4" />
                      ) : (
                        <SkillIcon className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium truncate ${
                          disabled
                            ? 'dark:text-claude-darkTextSecondary text-claude-textSecondary'
                            : isActive
                              ? 'text-claude-accent'
                              : 'dark:text-claude-darkText text-claude-text'
                        }`}>
                          {skill.name}
                        </span>
                        {skill.source === 'system' ? (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-500/10 text-green-600 dark:text-green-400 flex-shrink-0">
                            {i18nService.t('featured')}
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 flex-shrink-0">
                            {i18nService.t('personal')}
                          </span>
                        )}
                      </div>
                      <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary truncate mt-0.5">
                        {skill.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Footer - Manage Skills */}
      <div className="border-t dark:border-claude-darkBorder border-claude-border">
        <button
          onClick={handleManageSkills}
          className="w-full flex items-center justify-between px-4 py-3 text-sm dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors rounded-b-xl"
        >
          <span>{i18nService.t('manageSkills')}</span>
          <Cog6ToothIcon className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
        </button>
      </div>
    </div>
  );
};

export default SkillsPopover;
